import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { EMPTY_ARTICLE, slugify, setPath, withUid, buildPayload } from "./articleUtils";
import { ImageUploader } from "./ImageUploader";

export const ArticleEditor = ({ article, onCancel, onSaved }) => {
  const isEdit = Boolean(article);
  const [form, setForm] = useState(() => ({
    ...EMPTY_ARTICLE,
    ...(article || {}),
    outlook: article?.outlook || EMPTY_ARTICLE.outlook,
    closing: article?.closing || EMPTY_ARTICLE.closing,
    cta: article?.cta || EMPTY_ARTICLE.cta,
    // ensure repeatable rows have stable client ids so React keys aren't array indexes
    stats: (article?.stats || []).map((s) => (s._uid ? s : withUid(s))),
    donation_partners: (article?.donation_partners || []).map((p) => (p._uid ? p : withUid(p))),
    sections: (article?.sections || []).map((s) => (s._uid ? s : withUid(s))),
  }));
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const update = (path, value) => setForm((f) => setPath(f, Array.isArray(path) ? path : [path], value));

  const handleTitleChange = (v) => {
    setForm((f) => ({ ...f, title: v, slug: slugTouched ? f.slug : slugify(v) }));
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.slug.trim()) return toast.error("Slug is required");
    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (isEdit) {
        await http.put(`/articles/${article.slug}`, payload);
        toast.success("Article updated");
      } else {
        await http.post(`/articles`, payload);
        toast.success("Article published");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="article-editor" className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
      <button
        data-testid="editor-back"
        onClick={onCancel}
        className="inline-flex items-center gap-2 text-sm mb-8"
        style={{ color: "var(--wofy-text-soft)" }}
      >
        <ArrowLeft className="w-4 h-4" /> Admin dashboard
      </button>

      <div className="overline mb-3">{isEdit ? "EDIT ARTICLE" : "NEW ARTICLE"}</div>
      <h1 className="font-display text-4xl md:text-5xl leading-[1.05]">
        {isEdit ? form.title || "Untitled" : "Craft a new piece."}
      </h1>

      {/* Section + Basics */}
      <div className="mt-12 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3">
          <div className="overline">Meta</div>
        </div>
        <div className="lg:col-span-9 space-y-5">
          <Field label="Section">
            <select
              data-testid="field-section"
              value={form.section}
              onChange={(e) => update("section", e.target.value)}
              className="wofy-input"
            >
              <option value="change_maker">Change Maker</option>
              <option value="appeal">Emergency Appeal</option>
              <option value="story">Success Story</option>
            </select>
          </Field>

          <Field label="Title">
            <input
              data-testid="field-title"
              value={form.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="wofy-input"
              placeholder="e.g. Jane Doe & Ocean Guardians"
            />
          </Field>

          <Field label="URL slug" hint="Auto-generated from title. Edit if needed.">
            <input
              data-testid="field-slug"
              value={form.slug}
              onChange={(e) => { setSlugTouched(true); update("slug", slugify(e.target.value)); }}
              className="wofy-input"
              placeholder="my-article-slug"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Subtitle / Tagline">
              <input
                data-testid="field-subtitle"
                value={form.subtitle || ""}
                onChange={(e) => update("subtitle", e.target.value)}
                className="wofy-input"
                placeholder="Short teaser sentence"
              />
            </Field>
            <Field label="Location">
              <input
                data-testid="field-location"
                value={form.location || ""}
                onChange={(e) => update("location", e.target.value)}
                className="wofy-input"
                placeholder="Global · Founded 2010"
              />
            </Field>
          </div>

          <Field label="Overline" hint="Small caps kicker line above the title.">
            <input
              data-testid="field-overline"
              value={form.overline || ""}
              onChange={(e) => update("overline", e.target.value)}
              className="wofy-input"
              placeholder={form.section === "appeal" ? "EMERGENCY APPEAL · JUNE 2026" : "PROFILE · CHANGE MAKER"}
            />
          </Field>

          <Field label="Featured image (max 3MB)">
            <ImageUploader value={form.featured_image} onChange={(v) => update("featured_image", v)} />
          </Field>

          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid="field-published"
              checked={form.published}
              onChange={(e) => update("published", e.target.checked)}
              className="w-4 h-4"
            />
            <span>Published (visible to the public)</span>
          </label>
        </div>
      </div>

      {/* Section-specific fields */}
      {form.section === "change_maker" && (
        <SectionGroup title="Person / Organization">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Person name"><input data-testid="field-name" value={form.name || ""} onChange={(e) => update("name", e.target.value)} className="wofy-input" placeholder="José Andrés" /></Field>
            <Field label="Organization"><input data-testid="field-org" value={form.org || ""} onChange={(e) => update("org", e.target.value)} className="wofy-input" placeholder="World Central Kitchen" /></Field>
          </div>
          <Field label="Role"><input data-testid="field-role" value={form.role || ""} onChange={(e) => update("role", e.target.value)} className="wofy-input" placeholder="Founder & Chef" /></Field>
          <Field label="Pull quote"><textarea data-testid="field-quote" value={form.quote || ""} onChange={(e) => update("quote", e.target.value)} className="wofy-input min-h-[80px]" placeholder="Food is hope. Food is dignity." /></Field>
          <Field label="Quote attribution"><input data-testid="field-quote-attrib" value={form.quote_attrib || ""} onChange={(e) => update("quote_attrib", e.target.value)} className="wofy-input" placeholder="— José Andrés" /></Field>
        </SectionGroup>
      )}

      {form.section === "appeal" && (
        <SectionGroup title="Appeal details">
          <Field label="Urgency">
            <select data-testid="field-urgency" value={form.urgency || "critical"} onChange={(e) => update("urgency", e.target.value)} className="wofy-input">
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="ongoing">Ongoing</option>
            </select>
          </Field>
        </SectionGroup>
      )}

      {form.section === "story" && (
        <SectionGroup title="Story attribution">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Author name"><input data-testid="field-author" value={form.author_name || ""} onChange={(e) => update("author_name", e.target.value)} className="wofy-input" placeholder="Rachel M." /></Field>
            <Field label="Category"><input data-testid="field-category" value={form.category || ""} onChange={(e) => update("category", e.target.value)} className="wofy-input" placeholder="housing / education / food / …" /></Field>
          </div>
        </SectionGroup>
      )}

      {/* Body: Intro + Lead */}
      <SectionGroup title="Body">
        <Field label="Introduction" hint="First paragraph, large font on the page.">
          <textarea data-testid="field-intro" value={form.intro || ""} onChange={(e) => update("intro", e.target.value)} className="wofy-input min-h-[120px]" />
        </Field>
        <Field label="Lead paragraph" hint="Second paragraph, sets the stage.">
          <textarea data-testid="field-lead" value={form.lead || ""} onChange={(e) => update("lead", e.target.value)} className="wofy-input min-h-[100px]" />
        </Field>
      </SectionGroup>

      {/* Stats */}
      <SectionGroup
        title="Stat cards"
        onAdd={() => update("stats", [...form.stats, withUid({ k: "", v: "" })])}
        addLabel="Add stat"
      >
        {form.stats.length === 0 && <Empty text="No stat cards yet." />}
        {form.stats.map((s, i) => (
          <div key={s._uid} className="grid md:grid-cols-[1fr,2fr,auto] gap-3 items-center">
            <input data-testid={`stat-k-${i}`} placeholder="600M+" value={s.k} onChange={(e) => update(["stats", i, "k"], e.target.value)} className="wofy-input" />
            <input data-testid={`stat-v-${i}`} placeholder="Meals served since 2010" value={s.v} onChange={(e) => update(["stats", i, "v"], e.target.value)} className="wofy-input" />
            <RemoveBtn onClick={() => update("stats", form.stats.filter((_, j) => j !== i))} />
          </div>
        ))}
      </SectionGroup>

      {/* Sections */}
      <SectionGroup
        title="Body sections"
        hint="Each section shows as a two-column block with a heading and paragraphs."
        onAdd={() => update("sections", [...form.sections, withUid({ heading: "", body: [""], list: [], outro: "" })])}
        addLabel="Add section"
      >
        {form.sections.length === 0 && <Empty text="No sections yet. Click 'Add section' to start writing." />}
        {form.sections.map((sec, i) => (
          <div key={sec._uid} className="rounded-2xl border p-5 space-y-4" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
            <div className="flex items-center justify-between">
              <div className="overline">Section {i + 1}</div>
              <RemoveBtn onClick={() => update("sections", form.sections.filter((_, j) => j !== i))} />
            </div>
            <Field label="Heading">
              <input data-testid={`section-heading-${i}`} value={sec.heading} onChange={(e) => update(["sections", i, "heading"], e.target.value)} className="wofy-input" placeholder="A different approach to disaster relief" />
            </Field>
            <Field label="Paragraphs">
              <div className="space-y-3">
                {sec.body.map((p, j) => (
                  <div key={`${sec._uid}-p-${j}`} className="grid md:grid-cols-[1fr,auto] gap-3 items-start">
                    <textarea
                      data-testid={`section-${i}-body-${j}`}
                      value={p}
                      onChange={(e) => update(["sections", i, "body", j], e.target.value)}
                      className="wofy-input min-h-[80px]"
                    />
                    <RemoveBtn onClick={() => update(["sections", i, "body"], sec.body.filter((_, k) => k !== j))} />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => update(["sections", i, "body"], [...sec.body, ""])}
                  className="wofy-btn-ghost text-xs"
                  data-testid={`section-${i}-add-paragraph`}
                >
                  <Plus className="w-3 h-3" /> Add paragraph
                </button>
              </div>
            </Field>
            <Field label="Bullet list (optional)">
              <div className="space-y-2">
                {sec.list.map((li, j) => (
                  <div key={`${sec._uid}-l-${j}`} className="grid md:grid-cols-[1fr,auto] gap-2 items-center">
                    <input value={li} onChange={(e) => update(["sections", i, "list", j], e.target.value)} className="wofy-input" />
                    <RemoveBtn onClick={() => update(["sections", i, "list"], sec.list.filter((_, k) => k !== j))} />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => update(["sections", i, "list"], [...sec.list, ""])}
                  className="wofy-btn-ghost text-xs"
                >
                  <Plus className="w-3 h-3" /> Add bullet
                </button>
              </div>
            </Field>
            <Field label="Outro (optional)" hint="A softer closing paragraph shown in muted color.">
              <textarea value={sec.outro || ""} onChange={(e) => update(["sections", i, "outro"], e.target.value)} className="wofy-input min-h-[70px]" />
            </Field>
          </div>
        ))}
      </SectionGroup>

      {/* Appeal-only extras */}
      {form.section === "appeal" && (
        <>
          <SectionGroup
            title="Donation partners"
            hint="Trusted orgs shown on the appeal detail page."
            onAdd={() => update("donation_partners", [...form.donation_partners, withUid({ name: "", description: "", website: "", response: "" })])}
            addLabel="Add partner"
          >
            {form.donation_partners.length === 0 && <Empty text="No donation partners yet." />}
            {form.donation_partners.map((p, i) => (
              <div key={p._uid} className="rounded-2xl border p-5 space-y-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="flex justify-between items-center"><div className="overline">Partner {i + 1}</div><RemoveBtn onClick={() => update("donation_partners", form.donation_partners.filter((_, j) => j !== i))} /></div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input placeholder="Name" value={p.name} onChange={(e) => update(["donation_partners", i, "name"], e.target.value)} className="wofy-input" />
                  <input placeholder="Website URL" value={p.website} onChange={(e) => update(["donation_partners", i, "website"], e.target.value)} className="wofy-input" />
                </div>
                <input placeholder="Donate / response URL" value={p.response} onChange={(e) => update(["donation_partners", i, "response"], e.target.value)} className="wofy-input" />
                <textarea placeholder="Short description" value={p.description} onChange={(e) => update(["donation_partners", i, "description"], e.target.value)} className="wofy-input min-h-[70px]" />
              </div>
            ))}
          </SectionGroup>

          <SectionGroup
            title="How to help"
            onAdd={() => update("how_to_help", [...form.how_to_help, ""])}
            addLabel="Add tip"
          >
            {form.how_to_help.length === 0 && <Empty text="No tips yet." />}
            {form.how_to_help.map((t, i) => (
              <div key={`htoh-${i}`} className="grid md:grid-cols-[1fr,auto] gap-2 items-center">
                <input value={t} onChange={(e) => update(["how_to_help", i], e.target.value)} className="wofy-input" />
                <RemoveBtn onClick={() => update("how_to_help", form.how_to_help.filter((_, j) => j !== i))} />
              </div>
            ))}
          </SectionGroup>

          <SectionGroup title="Outlook block">
            <Field label="Heading"><input value={form.outlook?.heading || ""} onChange={(e) => update(["outlook", "heading"], e.target.value)} className="wofy-input" /></Field>
            <Field label="Paragraphs">
              <div className="space-y-2">
                {(form.outlook?.body || []).map((p, i) => (
                  <div key={`ob-${i}`} className="grid md:grid-cols-[1fr,auto] gap-2 items-start">
                    <textarea value={p} onChange={(e) => update(["outlook", "body", i], e.target.value)} className="wofy-input min-h-[70px]" />
                    <RemoveBtn onClick={() => update(["outlook", "body"], form.outlook.body.filter((_, j) => j !== i))} />
                  </div>
                ))}
                <button type="button" onClick={() => update(["outlook", "body"], [...(form.outlook?.body || []), ""])} className="wofy-btn-ghost text-xs">
                  <Plus className="w-3 h-3" /> Add paragraph
                </button>
              </div>
            </Field>
          </SectionGroup>

          <SectionGroup title="Closing block">
            <Field label="Heading"><input value={form.closing?.heading || ""} onChange={(e) => update(["closing", "heading"], e.target.value)} className="wofy-input" /></Field>
            <Field label="Paragraphs">
              <div className="space-y-2">
                {(form.closing?.body || []).map((p, i) => (
                  <div key={`cb-${i}`} className="grid md:grid-cols-[1fr,auto] gap-2 items-start">
                    <textarea value={p} onChange={(e) => update(["closing", "body", i], e.target.value)} className="wofy-input min-h-[70px]" />
                    <RemoveBtn onClick={() => update(["closing", "body"], form.closing.body.filter((_, j) => j !== i))} />
                  </div>
                ))}
                <button type="button" onClick={() => update(["closing", "body"], [...(form.closing?.body || []), ""])} className="wofy-btn-ghost text-xs">
                  <Plus className="w-3 h-3" /> Add paragraph
                </button>
              </div>
            </Field>
          </SectionGroup>

          <SectionGroup title="Disclaimer">
            <textarea data-testid="field-disclaimer" value={form.disclaimer || ""} onChange={(e) => update("disclaimer", e.target.value)} className="wofy-input min-h-[80px]" placeholder="Note about figures / data sources." />
          </SectionGroup>
        </>
      )}

      {/* Change maker CTA */}
      {form.section === "change_maker" && (
        <SectionGroup title="Call to action">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Label"><input value={form.cta?.label || ""} onChange={(e) => update(["cta", "label"], e.target.value)} className="wofy-input" placeholder="Visit World Central Kitchen" /></Field>
            <Field label="URL"><input value={form.cta?.href || ""} onChange={(e) => update(["cta", "href"], e.target.value)} className="wofy-input" placeholder="https://…" /></Field>
          </div>
        </SectionGroup>
      )}

      {/* Save */}
      <div className="sticky bottom-4 mt-16 flex justify-end gap-3 rounded-full p-3 shadow-lg" style={{ background: "rgba(253,251,247,0.95)", backdropFilter: "blur(12px)", border: "1px solid var(--wofy-border)" }}>
        <button data-testid="cancel-btn" onClick={onCancel} className="wofy-btn-ghost">Cancel</button>
        <button data-testid="save-article-btn" onClick={save} disabled={saving} className="wofy-btn-primary">
          {saving ? "Saving…" : isEdit ? "Save changes" : "Publish article"}
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-sm font-medium mb-1.5">{label}</div>
    {children}
    {hint && <div className="mt-1 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{hint}</div>}
  </label>
);

const SectionGroup = ({ title, hint, onAdd, addLabel, children }) => (
  <div className="mt-14 grid lg:grid-cols-12 gap-6">
    <div className="lg:col-span-3">
      <div className="overline">{title}</div>
      {hint && <div className="mt-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{hint}</div>}
      {onAdd && (
        <button type="button" onClick={onAdd} className="wofy-btn-ghost mt-4 text-xs" data-testid={`add-${title.toLowerCase().replace(/\s/g, "-")}`}>
          <Plus className="w-3 h-3" /> {addLabel || `Add ${title.toLowerCase()}`}
        </button>
      )}
    </div>
    <div className="lg:col-span-9 space-y-5">{children}</div>
  </div>
);

const RemoveBtn = ({ onClick }) => (
  <button type="button" onClick={onClick} className="wofy-btn-ghost self-start" style={{ color: "#B23A3A" }} aria-label="Remove">
    <Trash2 className="w-4 h-4" />
  </button>
);

const Empty = ({ text }) => (
  <div className="rounded-2xl border border-dashed p-6 text-sm text-center" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
    {text}
  </div>
);
